'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import { useTranslations } from 'next-intl';
import { AdornedInput, Box, Button, Dropdown, Slider, Text } from '@zillow/constellation-10';
import { ChevronDown } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useFilterLabels } from '@/src/lib/filters/useFilterLabels';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api/endpoints/area';
import { usePriceHistogram } from '@/src/lib/hooks/query/usePriceHistogramQuery';
import PriceHistogram from './PriceHistogram';
import { FILTER_DROPDOWN_MIN_WIDTH, FILTER_TRIGGER_LABEL_MIN_WIDTH } from './listingSearchConstants';

const PRICE_MIN = 0;
const PRICE_MAX = 10000;
const PRICE_STEP = 100;
// Trailing debounce for slider changes only: a drag fires per frame, and each
// applied change refetches /search/listings (no debounce downstream). Text
// inputs keep the faster after-paint flush — delaying them past one frame
// races a quick Enter/Search submit, which reads context filters synchronously.
export const SLIDER_APPLY_DEBOUNCE_MS = 200;

type PriceDropdownProps = {
  value: FilterState['price'];
  onChange: (next: FilterState['price']) => void;
  onReset: () => void;
  size?: 'sm' | 'md' | 'lg';
  /** Sibling filters — they narrow the rent distribution the histogram draws. */
  filters?: FilterState;
  /** Area the histogram is scoped to. */
  area?: AreaResponse | AutocompleteResult | null;
};

const PRICE_FORMATTER = new Intl.NumberFormat('en-US');

function formatInputValue(num: number | null): string {
  if (num === null) return '';
  return PRICE_FORMATTER.format(num);
}

function parseInputValue(raw: string): number | null {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits === '' ? null : parseInt(digits, 10);
}

/**
 * Price filter trigger + popover, built entirely from Constellation
 * primitives — `Dropdown` (trigger + portal + content + footer),
 * `Button` (trigger label + footer actions), `Input` (min/max),
 * `Slider`, `Text`, `Icon`. No HotPads custom wrappers.
 */
export default function PriceDropdown({ value, onChange, onReset, size = 'lg', filters, area }: PriceDropdownProps) {
  const tFilters = useTranslations('filters');
  const tSearch = useTranslations('search');
  const { getPriceButtonLabel } = useFilterLabels();
  const [isOpen, setIsOpen] = useState(false);

  // Applying a price change re-renders every FilterContext consumer (SRP grid,
  // map, pills) and writes the URL — far too heavy to run inside each slider
  // tick or keystroke (it was the page's INP ceiling). A local draft renders
  // the dropdown urgently; the real onChange flushes after the frame paints,
  // coalescing a burst of ticks into one apply.
  const [draft, setDraft] = useState<FilterState['price'] | null>(null);
  const shown = draft ?? value;
  const pendingRef = useRef<FilterState['price'] | null>(null);
  const flushScheduledRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const sliderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelSliderTimer = useCallback(() => {
    if (sliderTimerRef.current !== null) {
      clearTimeout(sliderTimerRef.current);
      sliderTimerRef.current = null;
    }
  }, []);

  const flushPending = useCallback(() => {
    cancelSliderTimer();
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending !== null) {
      onChangeRef.current(pending);
      setDraft(null);
    }
  }, [cancelSliderTimer]);

  const stagePending = useCallback((next: FilterState['price']) => {
    setDraft(next);
    pendingRef.current = next;
  }, []);

  const propagate = useCallback(
    (next: FilterState['price']) => {
      stagePending(next);
      cancelSliderTimer();
      if (flushScheduledRef.current) return;
      flushScheduledRef.current = true;
      void (async () => {
        await interactionResponse();
        flushScheduledRef.current = false;
        // A slider debounce armed after this flush was scheduled owns the pending value.
        if (sliderTimerRef.current !== null) return;
        flushPending();
      })();
    },
    [cancelSliderTimer, flushPending, stagePending],
  );

  const propagateFromSlider = useCallback(
    (next: FilterState['price']) => {
      stagePending(next);
      cancelSliderTimer();
      sliderTimerRef.current = setTimeout(flushPending, SLIDER_APPLY_DEBOUNCE_MS);
    },
    [cancelSliderTimer, flushPending, stagePending],
  );

  // Flush a pending change on unmount; close paths flush via handleOpenChange.
  useEffect(() => flushPending, [flushPending]);

  const hasActive = shown.min !== null || shown.max !== null;
  const label = getPriceButtonLabel(shown);

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      propagate({ ...shown, min: parseInputValue(e.target.value) });
    },
    [propagate, shown],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      propagate({ ...shown, max: parseInputValue(e.target.value) });
    },
    [propagate, shown],
  );

  const handleSliderChange = useCallback(
    (next: number | Array<number>) => {
      if (!Array.isArray(next)) return;
      const [lo, hi] = next;
      propagateFromSlider({
        min: lo === PRICE_MIN ? null : lo,
        max: hi === PRICE_MAX ? null : hi,
      });
    },
    [propagateFromSlider],
  );

  const handleReset = useCallback(() => {
    cancelSliderTimer();
    pendingRef.current = null;
    setDraft(null);
    onReset();
  }, [cancelSliderTimer, onReset]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) flushPending();
    },
    [flushPending],
  );

  const sliderValue: [number, number] = [shown.min ?? PRICE_MIN, shown.max ?? PRICE_MAX];
  const sliderTooltipPortal = useRef<HTMLDivElement>(null);

  // Only fetched once the popover has been opened — the trigger alone renders on
  // every page that hosts the search bar.
  const { data: histogram, isLoading: isHistogramLoading } = usePriceHistogram({
    filters: filters ?? DEFAULT_FILTERS,
    area,
    enabled: isOpen && !!filters,
  });

  return (
    <Dropdown.Root open={isOpen} onOpenChange={handleOpenChange} placement="bottom-start">
      <Dropdown.Trigger>
        <Button.Root
          tone={hasActive ? 'brand' : 'neutral'}
          emphasis="bare"
          size={size}
          css={{
            // Match the Location combobox's pill radius at every size.
            borderRadius: 'pill',
            justifyContent: 'space-between',
          }}
        >
          <Button.Label
            css={{
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
            // Pins the pill so it can't reflow as the label changes between
            // "Any price", "Over/Under $X", and "$X - $Y" — see
            // FILTER_TRIGGER_LABEL_MIN_WIDTH for the measurements. Sized on
            // this element specifically because `ch` resolves against its OWN
            // font: Button.Root inherits a plain regular-weight face, not this
            // Label's bold one.
            style={{ minWidth: FILTER_TRIGGER_LABEL_MIN_WIDTH }}
          >
            {label}
          </Button.Label>
          <Button.Icon>
            <LucideIcon icon={ChevronDown} size="sm" />
          </Button.Icon>
        </Button.Root>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content style={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}>
          <Dropdown.Body>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Text asChild textStyle="body-sm-bold" color="text.subtle">
                <p>{tFilters('priceRange')}</p>
              </Text>
              <Box css={{ paddingTop: 'tighter', paddingInline: 'tighter' }}>
                <PriceHistogram histogram={histogram} isLoading={isHistogramLoading} selection={shown} />
                {/* tooltip portal target — keeps the drag tooltip out of the visible DOM */}
                <div ref={sliderTooltipPortal} style={{ display: 'none' }} aria-hidden="true" />
                <Slider.Root
                  min={PRICE_MIN}
                  max={PRICE_MAX}
                  step={PRICE_STEP}
                  value={sliderValue}
                  onValueChange={handleSliderChange}
                  ariaLabel={({ index }) => (index === 0 ? tSearch('minimumPrice') : tSearch('maximumPrice'))}
                  ariaValuetext={({ valueNow }) => `$${PRICE_FORMATTER.format(valueNow)}`}
                >
                  <Slider.Track>
                    <Slider.ActiveTrack />
                  </Slider.Track>
                  <Slider.Handle tooltipPortalRoot={sliderTooltipPortal} />
                  <Slider.Handle tooltipPortalRoot={sliderTooltipPortal} />
                </Slider.Root>
              </Box>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                <AdornedInput
                  size="sm"
                  style={{ width: '11ch' }}
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  input={
                    <AdornedInput.Input
                      type="text"
                      inputMode="numeric"
                      placeholder={tFilters('noMin')}
                      aria-label={tSearch('minimumPrice')}
                      data-testid="filter-price-min"
                      value={formatInputValue(shown.min)}
                      onChange={handleMinChange}
                    />
                  }
                />
                <Text textStyle="body-sm" color="text.subtle">
                  {tFilters('to')}
                </Text>
                <AdornedInput
                  size="sm"
                  style={{ width: '11ch' }}
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  input={
                    <AdornedInput.Input
                      type="text"
                      inputMode="numeric"
                      placeholder={tFilters('noMax')}
                      aria-label={tSearch('maximumPrice')}
                      value={formatInputValue(shown.max)}
                      onChange={handleMaxChange}
                    />
                  }
                />
              </Box>
            </Box>
          </Dropdown.Body>
          <Dropdown.Footer
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: hasActive ? 'space-between' : 'flex-end',
              gap: 'tighter',
            }}
          >
            {hasActive ? (
              <Button tone="neutral" emphasis="outlined" size="sm" onClick={handleReset}>
                {tFilters('reset')}
              </Button>
            ) : null}
            <Dropdown.Close>
              <Button tone="brand" emphasis="filled" size="sm">
                {tFilters('done')}
              </Button>
            </Dropdown.Close>
          </Dropdown.Footer>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
